import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { cn } from '../../lib/utils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarChart = ({
  data = [],
  labels = null, // Custom labels for the x-axis
  xAxisLabel = '',
  yAxisLabel = '',
  title = '',
  showExpandIcon = true,
  onExpand,
  className = '',
  barColor = '#22c55e', // Default green color
  showGrid = true,
  maxValue = null,
  ...props
}) => {
  // Prepare data for Chart.js
  const prepareChartData = () => {
    if (!data || data.length === 0) {
      return {
        labels: ['No data'],
        datasets: [{
          label: yAxisLabel || 'Value',
          data: [0],
          backgroundColor: barColor,
        }],
      };
    }

    // Check if data is grouped (multiple values per item like { tasks: 10, issues: 5 })
    const firstItem = data[0];
    const isGroupedData = typeof firstItem === 'object' && firstItem !== null && 
                         !firstItem.hasOwnProperty('value') && 
                         Object.keys(firstItem).length > 1;
    
    if (isGroupedData) {
      // For grouped data like [{ tasks: 10, issues: 5 }, { tasks: 20, issues: 10 }]
      const keys = Object.keys(firstItem);
      const chartLabels = labels || Array.from({ length: data.length }, (_, i) => `Data ${i + 1}`);
      
      const datasets = keys.map((key, keyIndex) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1),
        data: data.map(item => item[key] || 0),
        backgroundColor: keyIndex === 0 ? barColor : '#1f2937',
      }));

      return {
        labels: chartLabels,
        datasets,
      };
    } else {
      // For simple data like [10, 20, 30] or [{ value: 10 }, { value: 20 }]
      const values = data.map(item => 
        typeof item === 'number' ? item : (item.value || 0)
      );
      
      const chartLabels = labels || Array.from({ length: values.length }, (_, i) => `Site ${i + 1}`);
      
      return {
        labels: chartLabels,
        datasets: [
          {
            label: yAxisLabel || 'Value',
            data: values,
            backgroundColor: barColor,
          },
        ],
      };
    }
  };

  const chartData = prepareChartData();

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: chartData.datasets?.length > 1,
        position: 'top',
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel,
        },
        grid: {
          display: showGrid,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        display: true,
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel,
        },
        grid: {
          display: showGrid,
        },
        ...(maxValue && { max: maxValue }),
      },
    },
  };

  return (
    <div className={cn(
      "p-4 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 flex flex-col justify-start items-start gap-3",
      showExpandIcon ? "inline-flex" : "w-full",
      className
    )} {...props}>
      {/* Header with title and expand icon */}
      <div className="self-stretch inline-flex justify-between items-end">
        <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-5">
          {title}
        </div>
        {showExpandIcon && (
          <div className="w-6 h-6 relative overflow-hidden">
            <button
              onClick={onExpand}
              className="w-4 h-4 left-[4px] top-[4px] absolute outline outline-2 outline-offset-[-1px] outline-black hover:opacity-80 transition-opacity"
            >
              <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Chart Container */}
      <div className="self-stretch flex-1 w-full overflow-hidden" style={{ height: '320px', minWidth: 0 }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default BarChart;
